import React from 'react';
import { Target, Eye, Award, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO, { SEO_CONFIGS } from '../components/SEO';

const About = () => {
  return (
    <div>
      <SEO {...SEO_CONFIGS.about} />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-primary text-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl lg:text-6xl font-bold mb-6"
          >
            About <span className="gold-text">Vocavi Partners</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-300 max-w-3xl"
          >
            Leading executive search firm based in Gurugram, India, specializing in C-suite and senior leadership recruitment.
          </motion.p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-spacing bg-white" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 mb-6">
                <Target size={32} className="text-secondary" />
              </div>
              <h2 className="text-3xl font-bold text-primary mb-4">Our Mission</h2>
              <p className="text-muted-foreground leading-relaxed">
                To connect visionary leaders with organizations poised for transformation. We believe that the right leadership can redefine industries and create lasting impact. Our mission is to identify, evaluate, and place exceptional executives who drive growth and innovation.
              </p>
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Eye size={32} className="text-accent" />
              </div>
              <h2 className="text-3xl font-bold text-primary mb-4">Our Vision</h2>
              <p className="text-muted-foreground leading-relaxed">
                To be India's most trusted executive search partner, recognized for our deep industry expertise, ethical practices, and commitment to excellence. We envision a future where every organization has access to transformational leadership.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-spacing bg-muted" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-primary mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Founded with a vision to transform executive recruitment in India, Vocavi Partners has grown to become a trusted name in leadership search across key industries.
                </p>
                <p>
                  With over 15 years of collective experience, our team has successfully placed more than 500 senior executives in organizations ranging from established enterprises to emerging market leaders.
                </p>
                <p>
                  We specialize in Manufacturing, Engineering, Automation, Fashion & Apparel, Energy, and Emerging Technology sectors, bringing deep domain expertise to every search assignment.
                </p>
              </div>
            </div>
            <div className="relative h-96 lg:h-full">
              <img
                src="https://images.unsplash.com/photo-1758599543152-a73184816eba?q=80&w=800&auto=format&fit=crop"
                alt="Vocavi Partners Team"
                className="w-full h-full object-cover shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-spacing bg-white" data-testid="our-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-primary mb-4">Our Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center" data-testid="value-excellence">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 mb-4">
                <Award size={32} className="text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Excellence</h3>
              <p className="text-muted-foreground text-sm">
                We pursue excellence in every aspect of our work, from research to placement.
              </p>
            </div>

            <div className="text-center" data-testid="value-integrity">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-4">
                <Users size={32} className="text-accent" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Integrity</h3>
              <p className="text-muted-foreground text-sm">
                Honesty and transparency form the foundation of all our relationships.
              </p>
            </div>

            <div className="text-center" data-testid="value-expertise">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 mb-4">
                <Target size={32} className="text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Expertise</h3>
              <p className="text-muted-foreground text-sm">
                Deep industry knowledge enables us to identify the right leaders.
              </p>
            </div>

            <div className="text-center" data-testid="value-partnership">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-4">
                <Eye size={32} className="text-accent" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Partnership</h3>
              <p className="text-muted-foreground text-sm">
                We build long-term relationships based on trust and mutual success.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;